import { useState, useEffect } from 'react';
import { supabase, getProfile, acceptEula } from './lib/supabase';
import EulaModal from './components/EulaModal';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import ProjectPage from './pages/EngagementPage';
import TeamPage from './pages/TeamPage';
import AuditorPage from './pages/AuditorPage';

// Google Fonts
const fontLink = document.createElement('link');
fontLink.rel = 'stylesheet';
fontLink.href = 'https://fonts.googleapis.com/css2?family=Roboto+Mono:wght@400;500&family=Montserrat:wght@400;600;700;800&display=swap';
document.head.appendChild(fontLink);

export default function App() {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [currentProject, setCurrentProject] = useState(null);
  const [showTeam, setShowTeam] = useState(false);
  const [eulaAccepted, setEulaAccepted] = useState(true); // true until profile loads
  const [dashKey, setDashKey] = useState(0);

  useEffect(() => {
    supabase.auth.getSession()
      .then(({ data: { session } }) => {
        setSession(session);
        if (session) loadProfile(session.user.id);
        else setAuthLoading(false);
      })
      .catch(err => {
        console.error('Failed to get session:', err);
        setAuthLoading(false);
      });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session) loadProfile(session.user.id);
      else { setProfile(null); setAuthLoading(false); }
    });

    return () => subscription.unsubscribe();
  }, []);

  const loadProfile = async (userId) => {
    try {
      const { data } = await getProfile(userId);
      setProfile(data);
      setEulaAccepted(data?.eula_accepted || false);
    } catch (err) {
      console.error('Failed to load profile:', err);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = () => {
    setSession(null);
    setProfile(null);
    setCurrentProject(null);
    setShowTeam(false);
    setEulaAccepted(true);
  };

  const handleEulaAccept = async () => {
    await acceptEula(session.user.id);
    setEulaAccepted(true);
  };

  if (authLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#0D2461',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: "'Roboto Mono', monospace",
        fontSize: '13px',
        color: 'rgba(255,255,255,0.5)',
        letterSpacing: '0.06em',
      }}>
        Loading…
      </div>
    );
  }

  if (!session) {
    return <LoginPage onLogin={(sess) => setSession(sess)} />;
  }

  if (!eulaAccepted) {
    return <EulaModal onAccept={handleEulaAccept} />;
  }

  if (showTeam === true) {
    return (
      <TeamPage
        session={session}
        profile={profile}
        onBack={() => setShowTeam(false)}
      />
    );
  }

  if (profile?.role === 'auditor' || showTeam === 'auditor') {
    return (
      <AuditorPage
        session={session}
        profile={profile}
        onBack={() => setShowTeam(false)}
      />
    );
  }

  if (currentProject) {
    return (
      <ProjectPage
        session={session}
        profile={profile}
        project={currentProject}
        onBack={() => {
          setCurrentProject(null);
          setDashKey(k => k + 1);
        }}
        onLogout={handleLogout}
      />
    );
  }

  return (
    <DashboardPage
      key={dashKey}
      session={session}
      profile={profile}
      onOpen={(proj) => setCurrentProject(proj)}
      onTeam={() => setShowTeam(true)}
      onAudit={() => setShowTeam('auditor')}
      onLogout={handleLogout}
    />
  );
}
